import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { WhatsAppChannelService } from '../service/core/WhatsAppChannelService.service';
import { AuthService } from '../service/core/auth.service';
import { WorkflowService } from '../service/core/workflow.service';
import { ChipModule } from 'primeng/chip';
import { DialogModule } from 'primeng/dialog';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MultiSelectModule } from 'primeng/multiselect';
import { ChannelWorkflowMappingService } from '../service/core/ChannelWorkflowMappingService.service';
import { InputTextModule } from 'primeng/inputtext';
import { SubscriptionService } from '@/pages/service/core/subscription.service';

/* ======================================================
   INTERFACES (LOCAL, UI-FOCUSED)
====================================================== */

/** One workflow chip shown in table */
export interface WorkflowChip {
  mappingId: string;
  workflowId: string;
  workflowName: string;
}

/** One channel row in the table */
export interface ChannelWithWorkflows {
  senderIdentifier: string;
  status: string;
  workflows: WorkflowChip[];
}

/** Workflow option shown in Add Mapping modal */
export interface WorkflowOption {
  id: string;
  flowName: string;
}

@Component({
    selector: 'app-channels',
    imports: [CommonModule,
        TabsModule,
        TableModule,
        ButtonModule,
        SelectModule,
        TagModule,
        FormsModule,
        ChipModule,
        DialogModule,
        DragDropModule,TableModule,
        MultiSelectModule,
        TooltipModule,
        InputTextModule],
    templateUrl: './channels.html',
    styleUrl: './channels.scss'
})
export class Channels {
    channels:any = [];
  searchTerm = '';
  sortBy: 'senderIdentifier' | 'status' | 'workflows' = 'senderIdentifier';
  sortDir: 'asc' | 'desc' = 'asc';
  isFreePlan = false;
  availableWorkflows: WorkflowOption[] = [];

  selectedChannel!: ChannelWithWorkflows;
  selectedWorkflowIds: string[] = [];

  showMappingDialog = false;
  loading = false;
  saving = false;

  // TODO: inject from auth context later
  companyId = '8f3a2c7e-4b6a-4a9d-9e2b-1c5f9a7d3b21';

  /** Live Meta number quality — phone/sender-id → rating (GREEN/YELLOW/RED). */
  numberQuality: Record<string, string> = {};

  constructor(
    private mappingService: ChannelWorkflowMappingService,
    private subscriptionService: SubscriptionService,
    private whatsAppService: WhatsAppChannelService
  ) {}

  // =========================
  // INIT
  // =========================
  ngOnInit(): void {
    this.isFreePlan = this.subscriptionService.isFreePlan();
    this.loadChannels();
  }

  // =========================
  // LOAD DATA
  // =========================
  loadChannels(): void {
    this.loading = true;

    this.mappingService
      .getChannelsWithWorkflows()
      .subscribe({
        next: (res:any) => {
          this.channels = res;
          this.loading = false;
          this.loadQuality();
        },
        error: () => {
          this.loading = false;
        }
      });
  }

  /** Pull live Meta quality for each connected number (read-only health badge). */
  loadQuality(): void {
    this.whatsAppService.getWhatsAppQuality().subscribe({
      next: (res: any) => { this.numberQuality = (res?.data ?? res)?.numbers ?? {}; },
      error: () => { /* best-effort — ignore failures */ }
    });
  }

  /** PrimeNG tag severity for a Meta quality rating. */
  qualitySeverity(rating: string | undefined | null): 'success' | 'warn' | 'danger' | 'secondary' {
    switch ((rating || '').toUpperCase()) {
      case 'GREEN':  return 'success';
      case 'YELLOW': return 'warn';
      case 'RED':    return 'danger';
      default:       return 'secondary';
    }
  }

  /** Friendly label for a Meta quality rating. */
  qualityLabel(rating: string | undefined | null): string {
    const r = (rating || '').toUpperCase();
    if (r === 'GREEN')  return 'High';
    if (r === 'YELLOW') return 'Medium';
    if (r === 'RED')    return 'Low';
    return r || 'Unknown';
  }

  // =========================
  // ADD MAPPING FLOW
  // =========================
  openAddMapping(channel:any): void {
    if (this.isFreePlan && this.channels.length >= 1) {
      return;
    }
    this.selectedChannel = channel;
    this.selectedWorkflowIds = [];
    this.showMappingDialog = true;

    this.mappingService
      .getActiveWorkflows()
      .subscribe(res => {
        // filter already-mapped workflows
        this.availableWorkflows = res.filter(w =>
          !channel.workflows.some((m:any) => m.workflowId === w.id)
        );
      });
  }

  closeDialog(): void {
    this.showMappingDialog = false;
    this.selectedWorkflowIds = [];
  }

  confirmMapping(): void {
    if (!this.selectedWorkflowIds.length) return;

    this.saving = true;

    this.mappingService.createMappings({
      senderIdentifier: this.selectedChannel.senderIdentifier,
      workflowIds: this.selectedWorkflowIds
    }).subscribe({
      next: (res:any) => {
        this.selectedChannel.workflows.push(...res);
        this.saving = false;
        this.showMappingDialog = false;
      },
      error: () => {
        this.saving = false;
      }
    });
  }

  // =========================
  // REMOVE MAPPING
  // =========================
  removeMapping(
    channel: ChannelWithWorkflows,
    workflow: WorkflowChip
  ): void {
    this.mappingService
      .deleteMapping(workflow.mappingId)
      .subscribe(() => {
        channel.workflows =
          channel.workflows.filter(w => w.mappingId !== workflow.mappingId);
      });
  }

  get maxChannelsReached(): boolean {
    return this.isFreePlan && this.channels.length >= 1;
  }

  get filteredChannels(): any[] {
    const term = this.searchTerm.trim().toLowerCase();
    const filtered = this.channels.filter((ch: any) => {
      if (!term) return true;
      const workflowNames = (ch.workflows || []).map((w: any) => w.workflowName).join(' ');
      const haystack = [
        ch.senderIdentifier,
        ch.status,
        workflowNames
      ]
        .filter(Boolean)
        .map((v: any) => String(v).toLowerCase())
        .join(' ');
      return haystack.includes(term);
    });

    const dir = this.sortDir === 'asc' ? 1 : -1;
    return filtered.sort((a: any, b: any) => {
      const aVal = this.getSortValue(a, this.sortBy);
      const bVal = this.getSortValue(b, this.sortBy);
      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });
  }

  setSort(key: 'senderIdentifier' | 'status' | 'workflows'): void {
    if (this.sortBy === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      return;
    }
    this.sortBy = key;
    this.sortDir = 'asc';
  }

  getSortIcon(key: 'senderIdentifier' | 'status' | 'workflows'): string {
    if (this.sortBy !== key) {
      return 'pi-sort-alt';
    }
    return this.sortDir === 'asc' ? 'pi-sort-amount-up-alt' : 'pi-sort-amount-down';
  }

  private getSortValue(item: any, key: 'senderIdentifier' | 'status' | 'workflows'): string | number {
    if (key === 'workflows') {
      return (item.workflows || []).length;
    }
    return (item?.[key] ?? '').toString().toLowerCase();
  }
}
